import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSync, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const ExperienceManager = () => {
  const [experiences, setExperiences] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentExperience, setCurrentExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const emptyExperience = {
    title: '',
    company_name: '',
    date: '',
    icon: '',
    iconBg: '#383E56',
    points: [''],
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const parseDate = (dateString) => {
    if (dateString.toLowerCase().includes('present')) {
      return new Date();
    }
    
    const parts = dateString.split('-').map(p => p.trim());
    const endDate = parts[parts.length - 1];
    
    if (endDate.toLowerCase().includes('present')) {
      return new Date();
    }
    
    const months = {
      jan: 0, january: 0,
      feb: 1, february: 1,
      mar: 2, march: 2,
      apr: 3, april: 3,
      may: 4,
      jun: 5, june: 5,
      jul: 6, july: 6,
      aug: 7, august: 7,
      sep: 8, september: 8,
      oct: 9, october: 9,
      nov: 10, november: 10,
      dec: 11, december: 11
    };
    
    const dateParts = endDate.split(' ').filter(p => p);
    if (dateParts.length >= 2) {
      const monthStr = dateParts[0].toLowerCase();
      const year = parseInt(dateParts[1]);
      const month = months[monthStr] || 0;
      return new Date(year, month);
    }
    
    return new Date(dateString);
  };

  const sortExperiences = (experiencesArray) => {
    return [...experiencesArray].sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });
  };

  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'experiences'));
      const experiencesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExperiences(sortExperiences(experiencesData));
    } catch (error) {
      console.error('Error fetching experiences:', error);
      alert('Failed to load experiences. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultExperiences = async () => {
    try {
      setLoading(true);
      const { experiences: defaultExperiences } = await import('../../Constants/index');
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const experience of defaultExperiences) {
        try {
          await addDoc(collection(db, 'experiences'), experience);
          successCount++;
        } catch (error) {
          console.error('Error adding experience:', experience.title, error);
          errorCount++;
        }
      }
      
      alert(`Loaded ${successCount} default experiences. ${errorCount} errors.`);
      await fetchExperiences();
    } catch (error) {
      console.error('Error loading defaults:', error);
      alert('Failed to load default experiences.');
    }
  };

  const handleAdd = () => {
    setCurrentExperience(emptyExperience);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEdit = (experience) => {
    setCurrentExperience({ ...experience });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;
    
    try {
      await deleteDoc(doc(db, 'experiences', id));
      await fetchExperiences();
      alert('Experience deleted successfully!');
    } catch (error) {
      console.error('Error deleting experience:', error);
      alert('Failed to delete experience.');
    }
  };

  const handleSave = async () => {
    if (!currentExperience.title || !currentExperience.company_name || !currentExperience.date) {
      alert('Please fill in all required fields (Title, Company, Date)');
      return;
    }

    try {
      if (isEditing) {
        const { id, ...experienceData } = currentExperience;
        await updateDoc(doc(db, 'experiences', id), experienceData);
        alert('Experience updated successfully!');
      } else {
        await addDoc(collection(db, 'experiences'), currentExperience);
        alert('Experience added successfully!');
      }
      
      setShowForm(false);
      setCurrentExperience(null);
      await fetchExperiences();
    } catch (error) {
      console.error('Error saving experience:', error);
      alert('Failed to save experience.');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setCurrentExperience(null);
  };

  const handlePointChange = (index, value) => {
    const updatedPoints = [...currentExperience.points];
    updatedPoints[index] = value;
    setCurrentExperience({ ...currentExperience, points: updatedPoints });
  };

  const addPoint = () => {
    setCurrentExperience({
      ...currentExperience,
      points: [...currentExperience.points, '']
    });
  };

  const removePoint = (index) => {
    const updatedPoints = currentExperience.points.filter((_, i) => i !== index);
    setCurrentExperience({ ...currentExperience, points: updatedPoints });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Experience Manager</h2>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchExperiences}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaSync />
            Refresh
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadDefaultExperiences}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            Load Defaults
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAdd}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaPlus />
            Add Experience
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {showForm && currentExperience && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-[#1d1836] p-6 rounded-xl mb-6 border border-purple-500/30"
          >
            <h3 className="text-xl font-bold text-white mb-4">
              {isEditing ? 'Edit Experience' : 'Add New Experience'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Title *</label>
                <input
                  type="text"
                  value={currentExperience.title}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, title: e.target.value })}
                  className="w-full bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                  placeholder="e.g., Software Development Intern"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Company *</label>
                <input
                  type="text"
                  value={currentExperience.company_name}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, company_name: e.target.value })}
                  className="w-full bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                  placeholder="e.g., OneSource Technologies"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Date *</label>
                <input
                  type="text"
                  value={currentExperience.date}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, date: e.target.value })}
                  className="w-full bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                  placeholder="e.g., May 2024 - July 2024 or June 2024 - Present"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Icon URL</label>
                <input
                  type="text"
                  value={currentExperience.icon}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, icon: e.target.value })}
                  className="w-full bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                  placeholder="URL to company logo"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Icon Background Color</label>
                <input
                  type="text"
                  value={currentExperience.iconBg}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, iconBg: e.target.value })}
                  className="w-full bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                  placeholder="#383E56"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-white mb-2">Key Points</label>
              {currentExperience.points.map((point, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={point}
                    onChange={(e) => handlePointChange(index, e.target.value)}
                    className="flex-1 bg-[#100d25] text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 outline-none"
                    placeholder={`Point ${index + 1}`}
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => removePoint(index)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                  >
                    <FaTrash />
                  </motion.button>
                </div>
              ))}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addPoint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mt-2"
              >
                <FaPlus />
                Add Point
              </motion.button>
            </div>

            <div className="flex gap-2 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                <FaSave />
                Save
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCancel}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                <FaTimes />
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-[#1d1836] rounded-xl p-4">
        <div className="text-white mb-4">
          Total Experiences: {experiences.length} | Sorted by date (newest first)
        </div>
        <div className="space-y-4">
          {experiences.map((experience) => (
            <motion.div
              key={experience.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#100d25] p-4 rounded-lg border border-gray-700 hover:border-purple-500 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">{experience.title}</h3>
                  <p className="text-purple-400">{experience.company_name}</p>
                  <p className="text-gray-400 text-sm">{experience.date}</p>
                  <ul className="mt-2 text-gray-300 text-sm list-disc ml-5">
                    {experience.points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-2 ml-4">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleEdit(experience)}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg"
                  >
                    <FaEdit />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleDelete(experience.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg"
                  >
                    <FaTrash />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-[#1d1836] p-4 rounded-xl border border-purple-500/30">
        <h3 className="text-lg font-bold text-white mb-2">Tips:</h3>
        <ul className="text-gray-300 text-sm list-disc ml-5 space-y-1">
          <li>Experiences are automatically sorted by end date (newest first)</li>
          <li>Use "Present" in date field for ongoing roles</li>
          <li>Format dates as: "Month YYYY - Month YYYY" or "Month YYYY - Present"</li>
          <li>Add multiple key points to describe your role and achievements</li>
          <li>Icon URLs should point to company logos (supports external URLs)</li>
        </ul>
      </div>
    </div>
  );
};

export default ExperienceManager;
